import { isAthenaGatewayError } from "../gateway/errors.ts";
import type { AthenaResult } from "../result/types.ts";
import { classifyAthenaError } from "./classify.ts";
import type { AthenaErrorKind } from "./types.ts";

export interface RetryNormalizedAthenaError {
  kind: AthenaErrorKind;
  retryable: boolean;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function isResult(value: unknown): value is AthenaResult<unknown> {
  return (
    isRecord(value) &&
    typeof value.status === "number" &&
    "data" in value &&
    "error" in value
  );
}

function isNormalizedError(value: unknown): value is {
  kind: AthenaErrorKind;
  message: string;
  status?: number;
} {
  return (
    isRecord(value) &&
    typeof value.message === "string" &&
    (value.kind === "unique_violation" ||
      value.kind === "not_found" ||
      value.kind === "validation" ||
      value.kind === "auth" ||
      value.kind === "rate_limit" ||
      value.kind === "transient" ||
      value.kind === "unknown") &&
    (value.status === undefined || typeof value.status === "number")
  );
}

function messageOf(value: unknown): string {
  if (typeof value === "string") {
    return value;
  }
  if (value instanceof Error) {
    return value.message;
  }
  if (isRecord(value)) {
    if (typeof value.message === "string") {
      return value.message;
    }
    if (typeof value.error === "string") {
      return value.error;
    }
  }
  return "";
}

function statusOf(value: unknown): number | undefined {
  return isRecord(value) && typeof value.status === "number"
    ? value.status
    : undefined;
}

function codeOf(value: unknown): "NETWORK_ERROR" | undefined {
  return isRecord(value) && value.gatewayCode === "NETWORK_ERROR"
    ? value.gatewayCode
    : undefined;
}

export function normalizeAthenaErrorForRetry(
  error: unknown
): RetryNormalizedAthenaError {
  if (isNormalizedError(error)) {
    return {
      kind: error.kind,
      retryable: error.kind === "transient" || error.kind === "rate_limit",
    };
  }

  if (isResult(error) && isNormalizedError(error.error)) {
    return {
      kind: error.error.kind,
      retryable:
        error.error.kind === "transient" || error.error.kind === "rate_limit",
    };
  }

  if (isAthenaGatewayError(error)) {
    const kind = classifyAthenaError(error.status, error.code, error.message);
    return {
      kind,
      retryable: kind === "transient" || kind === "rate_limit",
    };
  }

  const nested = isResult(error) ? error.error : error;
  const status = statusOf(error) ?? statusOf(nested);
  const message = messageOf(nested) || messageOf(error);
  const kind = classifyAthenaError(status, codeOf(error), message);
  return {
    kind,
    retryable: kind === "transient" || kind === "rate_limit",
  };
}
